import clsx from 'clsx';
import { format } from 'date-fns';
import { Clock, Loader2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { type DateRange } from 'react-day-picker';
import { FormattedMessage, useIntl } from 'react-intl';

import type { SourcingMetricsFilterRange } from 'api-clients/monolith';

import ChevronDownIcon from '@/assets/icons/chevron-down.svg';
import { CalendarIcon } from '@/components/icons/calendar';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { formatShortDate, lastDaysRange } from '@/utils/date-util';
import { getRangeTimeZoneLabel, localDayAt } from '@/utils/time-zone';

import { Tag } from './tag';
import { Typography } from './typography';

export const LIFETIME = 'lifetime' as const;

export type DateRangePreset = SourcingMetricsFilterRange | 'custom';

function computeDateRange(
  preset: Exclude<DateRangePreset, 'custom' | typeof LIFETIME>,
  localDays: boolean,
): DateRange {
  return lastDaysRange(Number(preset), localDays);
}

export function useDateRangePresetLabels(): Record<
  Exclude<DateRangePreset, 'custom'>,
  string
> {
  const intl = useIntl();

  return useMemo(
    () => ({
      '7': intl.formatMessage({
        defaultMessage: 'Last 7 days',
        description: 'Date range preset: filter analytics to the past week',
      }),
      '15': intl.formatMessage({
        defaultMessage: 'Last 15 days',
        description:
          'Date range preset: filter analytics to the past two weeks',
      }),
      '30': intl.formatMessage({
        defaultMessage: 'Last 30 days',
        description: 'Date range preset: filter analytics to the past month',
      }),
      '90': intl.formatMessage({
        defaultMessage: 'Last 90 days',
        description: 'Date range preset: filter analytics to the past quarter',
      }),
      lifetime: intl.formatMessage({
        defaultMessage: 'Lifetime',
        description:
          'Date range preset: show all-time analytics with no date bounds',
      }),
    }),
    [intl],
  );
}

interface DateRangePickerProps {
  selectedDate: DateRange;
  setSelectedDate: (range: DateRange) => void;
  selectedPreset?: DateRangePreset;
  onPresetChange?: (preset: DateRangePreset) => void;
  /** When set, called on Apply instead of separate preset/date callbacks. */
  onApply?: (params: { preset?: DateRangePreset; range: DateRange }) => void;
  className?: string;
  /** When false with presets enabled, the trigger shows formatted dates instead of the preset name. */
  showPresetInTrigger?: boolean;
  /** When false, the Lifetime preset is hidden from the preset list. Defaults to true. */
  showLifetimePreset?: boolean;
  /** Shows a spinner in place of the calendar icon while a newly-applied range is loading. */
  isLoading?: boolean;
  /** When true, a "Last N days" preset selects N calendar days ending today. */
  localDays?: boolean;
  /** The opening's IANA zone; when set, the popover shows the selected window's times in it. */
  timeZone?: string;
}

export function DateRangePicker({
  selectedDate,
  setSelectedDate,
  selectedPreset,
  onPresetChange,
  onApply,
  className,
  showPresetInTrigger = true,
  showLifetimePreset = true,
  isLoading = false,
  localDays = false,
  timeZone,
}: DateRangePickerProps) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<DateRange>(selectedDate);
  const [localPreset, setLocalPreset] = useState<DateRangePreset | undefined>(
    selectedPreset,
  );

  const hasPresets =
    selectedPreset !== undefined && onPresetChange !== undefined;

  const presetLabels = useDateRangePresetLabels();

  useEffect(() => {
    setDate(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    setLocalPreset(selectedPreset);
  }, [selectedPreset]);

  // A long custom range scans one day at a time for a daylight-saving change, so the
  // window is kept until a picked bound, the preset or the zone changes.
  const fromTime = date.from?.getTime();
  const toTime = date.to?.getTime();
  const isLifetime = localPreset === LIFETIME;
  const selectedWindow = useMemo(() => {
    if (!timeZone) return null;

    // Lifetime has no start; it runs up to today at the opening.
    if (isLifetime) {
      const today = localDayAt(new Date(), timeZone);
      const zone = today && getRangeTimeZoneLabel(today, today, timeZone);
      if (!today || !zone) return null;
      return {
        window: intl.formatMessage(
          {
            defaultMessage: 'Up to {end}, 11:59 PM',
            description:
              'Date range picker: the lifetime window, which has no start. {end} is today at the opening, such as Sep 16',
          },
          { end: format(today, 'LLL d') },
        ),
        zone,
      };
    }

    if (fromTime === undefined) return null;
    // A start picked on its own is one whole day.
    const from = new Date(fromTime);
    const to = new Date(toTime ?? fromTime);
    const zone = getRangeTimeZoneLabel(from, to, timeZone);
    if (!zone) return null;
    return {
      window: intl.formatMessage(
        {
          defaultMessage: '{start}, 12:00 AM – {end}, 11:59 PM',
          description:
            "Date range picker: the selected window in the opening's time zone. {start} and {end} are days such as Aug 18",
        },
        { start: format(from, 'LLL d'), end: format(to, 'LLL d') },
      ),
      zone,
    };
  }, [fromTime, toTime, isLifetime, timeZone, intl]);

  const presetOptions: Exclude<DateRangePreset, 'custom'>[] = [
    '7',
    '15',
    '30',
    '90',
    ...(showLifetimePreset ? [LIFETIME] : []),
  ];

  const buttonText = useMemo(() => {
    if (
      hasPresets &&
      showPresetInTrigger &&
      selectedPreset &&
      selectedPreset !== 'custom'
    ) {
      return presetLabels[selectedPreset];
    }

    if (
      hasPresets &&
      selectedPreset === LIFETIME &&
      !selectedDate.from &&
      !selectedDate.to
    ) {
      return presetLabels.lifetime;
    }

    if (selectedDate.from && selectedDate.to) {
      return `${formatShortDate(selectedDate.from)} - ${formatShortDate(
        selectedDate.to,
      )}`;
    }

    return intl.formatMessage({
      defaultMessage: 'Pick a range',
      description:
        'Placeholder before user selects start/end in calendar popover',
    });
  }, [
    hasPresets,
    showPresetInTrigger,
    selectedPreset,
    presetLabels,
    selectedDate,
    intl,
  ]);

  const handlePresetClick = (preset: Exclude<DateRangePreset, 'custom'>) => {
    setLocalPreset(preset);
    if (preset === LIFETIME) {
      setDate({ from: undefined, to: undefined });
    } else {
      setDate(computeDateRange(preset, localDays));
    }
  };

  const handleCustomClick = () => {
    setLocalPreset('custom');
    setDate({ from: undefined, to: undefined });
  };

  const handleApply = () => {
    const range =
      localPreset === LIFETIME
        ? onApply
          ? { from: undefined, to: undefined }
          : { from: new Date(0), to: new Date() }
        : date;

    if (onApply) {
      onApply({
        preset: hasPresets ? localPreset : undefined,
        range,
      });
      setSelectedDate(range);
      if (hasPresets && localPreset) {
        onPresetChange?.(localPreset);
      }
    } else {
      if (hasPresets && localPreset) {
        onPresetChange?.(localPreset);
      }
      setSelectedDate(range);
    }
    setOpen(false);
  };

  const handleClose = () => {
    setDate(selectedDate);
    setLocalPreset(selectedPreset);
    setOpen(false);
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setOpen(true);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id="date"
          variant="ghost"
          className={clsx('font-normal', className)}
          onClick={handleClick}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <CalendarIcon />
          )}
          <span>{buttonText}</span>
          <img src={ChevronDownIcon} alt="" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex flex-col">
          <div className="flex">
            {hasPresets && (
              <div className="flex w-[192px] flex-col border-r">
                <button
                  type="button"
                  className={clsx(
                    'px-4 py-2 text-left whitespace-nowrap',
                    'hover:bg-fx-neutral-100',
                    localPreset === 'custom' && 'bg-fx-neutral-100 font-medium',
                  )}
                  onClick={handleCustomClick}
                >
                  <Typography variant="subtitle1">
                    <FormattedMessage
                      defaultMessage="Custom"
                      description="Date range preset: open calendar for manual date selection"
                    />
                  </Typography>
                </button>
                {presetOptions.map(preset => (
                  <button
                    key={preset}
                    type="button"
                    className={clsx(
                      'px-4 py-2 text-left text-sm whitespace-nowrap',
                      'hover:bg-fx-neutral-100',
                      localPreset === preset && 'bg-fx-brand-50 font-medium',
                    )}
                    onClick={() => handlePresetClick(preset)}
                  >
                    <Typography variant="subtitle1">
                      {presetLabels[preset]}
                    </Typography>
                  </button>
                ))}
              </div>
            )}
            <div className="flex flex-col">
              <Calendar
                autoFocus
                mode="range"
                required
                selected={date}
                onSelect={(range: DateRange) => {
                  setDate(range);
                  if (hasPresets) setLocalPreset('custom');
                }}
                numberOfMonths={2}
              />
            </div>
          </div>
          {selectedWindow ? (
            // Close sits under the presets, the window under the calendar.
            <div className="flex items-center p-2 pb-0">
              <div className={clsx('shrink-0', hasPresets && 'w-[192px]')}>
                <Button variant="outline" onClick={handleClose}>
                  <Typography variant="body2">
                    <FormattedMessage
                      defaultMessage="Close"
                      description="Date range popover: close without applying changes"
                    />
                  </Typography>
                </Button>
              </div>
              <div
                className="flex min-w-0 flex-1 flex-col items-start gap-1"
                data-testid="date-range-picker-window"
              >
                <Typography variant="body2" className="whitespace-nowrap">
                  {selectedWindow.window}
                </Typography>
                <Tag
                  className="border-fx-neutral-200 border bg-white"
                  icon={
                    <Clock aria-hidden className="text-fx-neutral-500 size-3" />
                  }
                  text={
                    <Typography as="span" variant="small2">
                      {selectedWindow.zone}
                    </Typography>
                  }
                />
              </div>
              <Button onClick={handleApply}>
                <Typography variant="body2" className="text-white">
                  <FormattedMessage
                    defaultMessage="Apply"
                    description="Date range popover: commit selected date range"
                  />
                </Typography>
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between p-2 pb-0">
              <Button variant="outline" onClick={handleClose}>
                <Typography variant="body2">
                  <FormattedMessage
                    defaultMessage="Close"
                    description="Date range popover: close without applying changes"
                  />
                </Typography>
              </Button>
              <Button onClick={handleApply}>
                <Typography variant="body2" className="text-white">
                  <FormattedMessage
                    defaultMessage="Apply"
                    description="Date range popover: commit selected date range"
                  />
                </Typography>
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
